import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { putUiPrefs, type ThemeName, type Density } from '../api/uiPrefs';

/** Sidebar sub-menu rendering style. 'accordion' grows inline under the
 *  section header (legacy), 'flyout' opens a side-anchored Popper that
 *  spotlights one section at a time. */
export type SubmenuStyle = 'accordion' | 'flyout';

interface UiPrefsState {
  theme: ThemeName;
  density: Density;
  submenuStyle: SubmenuStyle;
  /** The single open section's key, or null if every section closed. */
  openSectionKey: string | null;
  /** Routes the user has pinned to the top of the sidebar. */
  pinnedRoutes: string[];
  /** Routes the user recently navigated to — most-recent-first, capped
   *  at 12 to keep the "recent" rail compact. */
  recentRoutes: string[];
  /** Last shipped "what's new" version the user has seen. Null on a
   *  fresh install so the popup shows once. */
  whatsNewSeenVersion: string | null;

  setTheme: (name: ThemeName) => Promise<void>;
  setDensity: (d: Density) => Promise<void>;
  setSubmenuStyle: (s: SubmenuStyle) => void;
  setOpenSectionKey: (k: string | null) => void;
  toggleSection: (k: string) => void;
  pinRoute: (path: string) => void;
  unpinRoute: (path: string) => void;
  trackVisit: (path: string) => void;
  markWhatsNewSeen: (version: string) => void;
  reset: () => void;
  hydrate: (prefs: { ui_theme: ThemeName; ui_density: Density }) => void;
}

const RECENT_CAP = 12;

/** Local mirror of the user's server-side UI preferences.
 *
 * Persisted to localStorage so the right theme is applied on the very
 * first paint (no flash of light theme). On login / page load we
 * `hydrate` from the GET /me/ui-prefs response to overwrite any drift.
 */
export const useUiPrefsStore = create<UiPrefsState>()(
  persist(
    (set) => ({
      theme: 'modern',
      density: 'comfortable',
      // Default to flyout — the eye-catching panel feels right for
      // first-time users; the office system uses the same default.
      // The user can still pick אקורדיון from the sidebar's palette
      // button or from /personalize.
      submenuStyle: 'flyout',
      openSectionKey: 'main',
      pinnedRoutes: [],
      recentRoutes: [],
      whatsNewSeenVersion: null,
      setTheme: async (name) => {
        set({ theme: name });
        try { await putUiPrefs({ ui_theme: name }); } catch { /* fail silent */ }
      },
      setDensity: async (d) => {
        set({ density: d });
        try { await putUiPrefs({ ui_density: d }); } catch { /* fail silent */ }
      },
      setSubmenuStyle: (s) => set({ submenuStyle: s, openSectionKey: null }),
      setOpenSectionKey: (k) => set({ openSectionKey: k }),
      toggleSection: (k) =>
        set((state) => ({ openSectionKey: state.openSectionKey === k ? null : k })),
      pinRoute: (path) =>
        set((s) => ({
          pinnedRoutes: s.pinnedRoutes.includes(path)
            ? s.pinnedRoutes
            : [...s.pinnedRoutes, path],
        })),
      unpinRoute: (path) =>
        set((s) => ({ pinnedRoutes: s.pinnedRoutes.filter((p) => p !== path) })),
      trackVisit: (path) =>
        set((s) => {
          // Skip login / SSO / setup-org / utility paths so the rail
          // only shows actual feature pages the user works on.
          if (!path || path === '/' || path.startsWith('/login') || path.startsWith('/auth/')) {
            return s;
          }
          const without = s.recentRoutes.filter((p) => p !== path);
          return { recentRoutes: [path, ...without].slice(0, RECENT_CAP) };
        }),
      markWhatsNewSeen: (version) => set({ whatsNewSeenVersion: version }),
      reset: () => set({
        submenuStyle: 'accordion',
        openSectionKey: 'main',
        pinnedRoutes: [],
        recentRoutes: [],
        whatsNewSeenVersion: null,
      }),
      hydrate: (prefs) => set({ theme: prefs.ui_theme, density: prefs.ui_density }),
    }),
    {
      name: 'maven_ui_prefs',
      // Bumping the persist version triggers a migration. Used here
      // to flip the sidebar style default from accordion to flyout
      // for existing browsers without wiping the rest of the user's
      // prefs (theme, density, pinned routes, recents).
      version: 2,
      migrate: (state: any, fromVersion: number) => {
        if (!state) return state;
        if (fromVersion < 2) {
          // Default flip — but if the user explicitly picked
          // accordion before, the previous render had no migration so
          // we can't tell. Conservative: leave anything non-default
          // alone; only flip if the stored value matches the OLD
          // default.
          if (state.submenuStyle === 'accordion') {
            state.submenuStyle = 'flyout';
            state.openSectionKey = null;
          }
        }
        return state;
      },
    },
  ),
);
